import { useCallback, useState } from 'react';

import { SERIES_COLORS, STATUS_COLORS } from '../lib/chart-palette';
import { useAdminData } from '../lib/use-admin-data';
import { fetchAdminLive, type LiveMetrics } from '../services/api';
import { Feedback } from './Feedback';
import { StatTile, type StatTone } from './StatTile';
import { TimeSeriesChart } from './TimeSeriesChart';

/**
 * Aba AO VIVO — o que está acontecendo agora, com resolução de 1 segundo.
 *
 * Estes números vêm da MEMÓRIA da API, não do banco. É uma escolha
 * deliberada: sob teste de carga o Postgres é o primeiro a saturar, e um
 * painel que dependesse dele deixaria de responder no exato momento em que
 * ele precisa mostrar o que está acontecendo.
 */

/** Janelas de observação, em segundos (limitadas ao que a API guarda). */
const WINDOWS = [
  { seconds: 60, label: '1 min' },
  { seconds: 180, label: '3 min' },
  { seconds: 300, label: '5 min' },
];

const REFRESH_MS = 2_000;

/** Acima disto, a taxa de erro deixa de ser ruído e vira incidente. */
const ERROR_RATE_WARNING = 0.01;
const ERROR_RATE_CRITICAL = 0.05;

export function AdminLiveTab() {
  const [windowSeconds, setWindowSeconds] = useState(60);

  const loader = useCallback(() => fetchAdminLive(windowSeconds), [windowSeconds]);
  const { data, error, isLoading } = useAdminData<LiveMetrics>(loader, REFRESH_MS);

  if (error) return <Feedback kind="error">{error}</Feedback>;
  if (isLoading && !data) return <p className="page-loading">Medindo…</p>;
  if (!data) return null;

  const labels = data.series.map((point) => point.time);

  return (
    <div className="admin-tab">
      <div className="filter-bar" role="group" aria-label="Janela de observação">
        {WINDOWS.map((option) => (
          <button
            key={option.seconds}
            type="button"
            className={`filter-bar__option${
              windowSeconds === option.seconds ? ' filter-bar__option--active' : ''
            }`}
            onClick={() => setWindowSeconds(option.seconds)}
          >
            {option.label}
          </button>
        ))}
        <span className="admin-tab__pulse" aria-live="polite">
          atualizando a cada {REFRESH_MS / 1000}s
        </span>
      </div>

      <div className="stat-grid">
        <StatTile
          hero
          label="Requisições por segundo"
          value={data.requestsPerSecond.toLocaleString('pt-BR')}
          detail={`${data.totalRequests.toLocaleString('pt-BR')} na janela`}
        />
        <StatTile
          label="Taxa de erro"
          value={formatPercent(data.errorRate)}
          tone={errorTone(data.errorRate)}
          detail="respostas 4xx e 5xx"
        />
        <StatTile
          label="Latência p95"
          value={`${data.p95Ms} ms`}
          // Ao vivo o percentil vem de um histograma de custo fixo — preciso o
          // suficiente para decidir "está lento?", e é honesto dizer isso.
          detail={`p99 ${data.p99Ms} ms · aproximado`}
        />
        <StatTile
          label="Pico de resposta"
          value={`${data.maxDurationMs} ms`}
          detail="requisição mais lenta da janela"
        />
        <StatTile
          label="Conexões ao vivo"
          value={data.liveConnections.total.toLocaleString('pt-BR')}
          detail={`${data.liveConnections.overlay} overlay · ${data.liveConnections.moderator} moderador`}
        />
        <StatTile
          label="Memória da API"
          value={`${data.memoryRssMb} MB`}
          detail={`pool do banco: ${data.databasePool.total} conexões, ${data.databasePool.waiting} na fila`}
          tone={data.databasePool.waiting > 0 ? 'warning' : 'neutral'}
        />
        <StatTile
          label="Tráfego sintético"
          value={formatPercent(data.syntheticShare)}
          detail="parcela vinda do teste de carga"
        />
        <StatTile
          label="Telemetria descartada"
          value={data.droppedWrites.toLocaleString('pt-BR')}
          tone={data.droppedWrites > 0 ? 'warning' : 'neutral'}
          detail={
            data.droppedWrites > 0
              ? 'o banco não acompanhou a escrita — os números ao vivo seguem completos'
              : 'nenhum acesso perdido'
          }
        />
      </div>

      <TimeSeriesChart
        title="Requisições por segundo"
        description="Cada ponto é um segundo real, medido na memória da API."
        labels={labels}
        filled
        series={[
          {
            key: 'total',
            label: 'Requisições',
            color: SERIES_COLORS[0],
            values: data.series.map((point) => point.total),
          },
        ]}
      />

      <TimeSeriesChart
        title="Respostas por classe de status"
        description="Erro de servidor (5xx) subindo é o primeiro sinal de saturação."
        labels={labels}
        series={[
          {
            key: 'ok',
            label: 'Sucesso (2xx/3xx)',
            color: STATUS_COLORS.good,
            values: data.series.map((point) => point.ok),
          },
          {
            key: 'client',
            label: 'Erro do cliente (4xx)',
            color: STATUS_COLORS.warning,
            values: data.series.map((point) => point.clientError),
          },
          {
            key: 'server',
            label: 'Erro do servidor (5xx)',
            color: STATUS_COLORS.critical,
            values: data.series.map((point) => point.serverError),
          },
        ]}
      />

      <TimeSeriesChart
        title="Tempo de resposta"
        description="A média esconde o pior caso — por isso o máximo aparece junto."
        labels={labels}
        formatValue={(value) => `${Math.round(value)} ms`}
        series={[
          {
            key: 'avg',
            label: 'Média',
            color: SERIES_COLORS[0],
            values: data.series.map((point) => point.avgDurationMs ?? 0),
          },
          {
            key: 'max',
            label: 'Máximo',
            color: SERIES_COLORS[1],
            values: data.series.map((point) => point.maxDurationMs ?? 0),
          },
        ]}
      />
    </div>
  );
}

/** 0.0342 → "3,42%" */
function formatPercent(ratio: number): string {
  return `${(ratio * 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
}

function errorTone(errorRate: number): StatTone {
  if (errorRate >= ERROR_RATE_CRITICAL) return 'critical';
  if (errorRate >= ERROR_RATE_WARNING) return 'warning';
  return 'good';
}
